import * as React from 'react'
import { Modal } from './Modal'
import { cn } from '../lib/utils'

export interface RueckfrageAktion {
  /** Beschriftung — das Verb der Handlung („Abmelden", „Verwerfen"), nie „OK". */
  label: string
  /** `gefahr`: zerstört etwas (rot), `haupt`: die empfohlene Handlung, sonst neutral. */
  art?: 'haupt' | 'gefahr' | 'neutral'
  onClick: () => void | Promise<void>
  disabled?: boolean
}

export interface RueckfrageProps {
  offen: boolean
  titel: React.ReactNode
  /** Der Text der Frage; darf eine Liste enthalten. */
  children?: React.ReactNode
  aktionen: RueckfrageAktion[]
  /** „Abbrechen" — schließt ohne Handlung. */
  abbrechenLabel?: string
  onAbbrechen: () => void
}

/**
 * AP-46 8.3 — **Rückfrage vor einer folgenreichen Handlung**, für ERP und PWA. Die PWA hatte bis dahin
 * keine Dialog-Komponente und fragte mit `window.confirm` — das ist verboten (AP-35 Nachtrag
 * Bearbeitbarkeit), weil der Browser-Dialog Texte abschneidet, auf dem Telefon nicht zur App passt
 * und keine dritte Wahl kennt („Erst übertragen" · „Abmelden — Daten bleiben" · „Abbrechen").
 *
 * Schaltflächen mindestens 48 px hoch (BD-42: Handschuhe, Sonne).
 */
export function Rueckfrage({ offen, titel, children, aktionen, abbrechenLabel = 'Abbrechen', onAbbrechen }: RueckfrageProps) {
  const [laeuft, setLaeuft] = React.useState<number | null>(null)
  return (
    <Modal
      isOpen={offen}
      onClose={() => laeuft === null && onAbbrechen()}
      title={titel}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2">
          {aktionen.map((a, i) => (
            <button
              key={a.label}
              type="button"
              disabled={a.disabled || laeuft !== null}
              onClick={async () => {
                setLaeuft(i)
                try {
                  await a.onClick()
                } finally {
                  setLaeuft(null)
                }
              }}
              className={cn(
                'min-h-12 w-full rounded-lg px-4 text-base font-semibold disabled:opacity-50',
                a.art === 'gefahr' && 'bg-red-600 text-white',
                a.art === 'haupt' && 'bg-primary text-primary-foreground',
                (!a.art || a.art === 'neutral') && 'border border-input bg-background text-foreground',
              )}
            >
              {laeuft === i ? '…' : a.label}
            </button>
          ))}
          <button type="button" onClick={onAbbrechen} disabled={laeuft !== null} className="min-h-12 w-full rounded-lg px-4 text-base text-muted-foreground">
            {abbrechenLabel}
          </button>
        </div>
      }
    >
      <div className="text-sm text-foreground">{children}</div>
    </Modal>
  )
}
